import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, PenLine, Type, Upload, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import NdaSignaturePad, { SignaturePadHandle } from "./NdaSignaturePad";
import { signNdaAsSs } from "../../utils/api/endpoints/questionnaire";

interface SsCountersignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (ssName: string, signedAt: string, pdfPath: string | null) => void;
  responseId: string;
  defaultSignerName: string;
}

type SignatureType = "draw" | "type" | "upload";

const FONT_OPTIONS = [
  { name: "Elegant Cursive", value: "54px 'Dancing Script', cursive", class: "font-dancing" },
  { name: "Modern Script", value: "50px 'Caveat', cursive", class: "font-caveat" },
  { name: "Playful Signature", value: "44px 'Pacifico', cursive", class: "font-pacifico" }
];

export const SsCountersignModal: React.FC<SsCountersignModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  responseId,
  defaultSignerName
}) => {
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const [signerName, setSignerName] = useState(defaultSignerName || "");
  const [sigType, setSigType] = useState<SignatureType>("draw");
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0]);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSignerName(defaultSignerName || "");
      setSigType("draw");
      setUploadedImage(null);
      setHasSignature(false);
      setIsSubmitting(false);
      setErrorMsg("");
      setSuccess(false);

      // Load fonts
      const linkId = "scoping-nda-fonts";
      if (!document.getElementById(linkId)) {
        const link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        link.href =
          "https://fonts.googleapis.com/css2?family=Caveat:wght@700&family=Dancing+Script:wght@700&family=Pacifico&display=swap";
        document.head.appendChild(link);
      }
    }
  }, [isOpen, defaultSignerName]);

  const generateTypedSignaturePng = (text: string, fontVal: string): string => {
    const canvas = document.createElement("canvas");
    canvas.width = 500;
    canvas.height = 150;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = fontVal;
    ctx.fillStyle = "#0f1f5c";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMsg("Please upload an image file (PNG/JPG).");
      return;
    }

    if (file.size > 500 * 1024) {
      setErrorMsg("Image size must be less than 500 KB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result as string);
      setErrorMsg("");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!signerName.trim()) {
      setErrorMsg("Please enter your name.");
      return;
    }

    let signatureBase64 = "";

    if (sigType === "draw") {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        setErrorMsg("Please draw your signature.");
        return;
      }
      signatureBase64 = signaturePadRef.current.toDataURL("image/png");
    } else if (sigType === "type") {
      signatureBase64 = generateTypedSignaturePng(signerName.trim(), selectedFont.value);
    } else {
      if (!uploadedImage) {
        setErrorMsg("Please upload a signature image.");
        return;
      }
      signatureBase64 = uploadedImage;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const res = await signNdaAsSs(responseId, {
        ssSignerName: signerName.trim(),
        ssSignatureBase64: signatureBase64
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess(res.nda_ss_signer_name, res.nda_ss_signed_at, res.nda_pdf_path);
      }, 1500);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || "Failed to counter-sign NDA.");
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] border border-slate-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-150 flex items-center justify-center text-indigo-650">
              <PenLine size={16} />
            </div>
            <h3 className="font-bold text-slate-800 text-sm">Counter-Sign Agreement</h3>
          </div>
          {!isSubmitting && !success && (
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-650 transition cursor-pointer">
              <X size={16} />
            </button>
          )}
        </div>

        {success ? (
          <div className="p-10 flex flex-col items-center justify-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-300 flex items-center justify-center text-emerald-500 animate-bounce">
              <CheckCircle2 size={36} />
            </div>
            <div>
              <h4 className="font-bold text-slate-800 text-base">Agreement Counter-Signed</h4>
              <p className="text-xs text-slate-500 mt-1">
                The NDA is now fully executed and the client has been notified.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4 overflow-y-auto">
            {errorMsg && (
              <div className="p-3 bg-rose-50 border border-rose-150 text-rose-700 text-xs rounded-xl flex items-center gap-2">
                <AlertCircle size={14} className="shrink-0" />
                <span className="font-medium leading-tight">{errorMsg}</span>
              </div>
            )}

            {/* Name Input */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-600">Solutions Specialist Name</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-3.5 py-2 text-xs border border-slate-250 rounded-xl focus:border-indigo-550 focus:ring-1 focus:ring-indigo-550 outline-none"
              />
            </div>

            {/* Signature Method Tabs */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-slate-600">Signature Method</label>
              <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-xl">
                {(["draw", "type", "upload"] as SignatureType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSigType(type)}
                    className={`py-1.5 rounded-lg text-xs font-bold transition capitalize cursor-pointer ${
                      sigType === type ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Interactive Areas */}
            {sigType === "draw" && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500">Draw Signature below</span>
                  <button
                    type="button"
                    onClick={() => {
                      signaturePadRef.current?.clear();
                      setHasSignature(false);
                    }}
                    className="text-[10px] font-bold text-indigo-650 hover:underline cursor-pointer"
                  >
                    Clear Canvas
                  </button>
                </div>
                <div className="h-32 border border-slate-200 rounded-xl bg-slate-50/50 overflow-hidden relative">
                  <NdaSignaturePad
                    ref={signaturePadRef}
                    onEnd={() => setHasSignature(true)}
                    onBegin={() => setHasSignature(true)}
                  />
                </div>
              </div>
            )}

            {sigType === "type" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500">Preview typed signature</span>
                </div>
                <div className="h-20 border border-slate-200 rounded-xl bg-slate-50/50 flex items-center justify-center overflow-hidden">
                  {signerName.trim() ? (
                    <span className={`${selectedFont.class} text-slate-850 tracking-wider text-2xl`}>
                      {signerName.trim()}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Type your name above...</span>
                  )}
                </div>
                {/* Font Selector */}
                <div className="flex flex-wrap gap-1.5">
                  {FONT_OPTIONS.map((f) => (
                    <button
                      key={f.name}
                      type="button"
                      onClick={() => setSelectedFont(f)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition cursor-pointer ${
                        selectedFont.name === f.name
                          ? "bg-indigo-50 border-indigo-250 text-indigo-750"
                          : "bg-white border-slate-200 text-slate-650 hover:bg-slate-50"
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sigType === "upload" && (
              <div className="space-y-1.5">
                <span className="block text-[11px] font-bold text-slate-500">Upload signature image</span>
                {uploadedImage ? (
                  <div className="relative border border-slate-200 rounded-xl p-3 bg-slate-50/50 flex items-center justify-between">
                    <img src={uploadedImage} alt="Uploaded signature" className="max-h-12 object-contain" />
                    <button
                      type="button"
                      onClick={() => setUploadedImage(null)}
                      className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-slate-250 hover:border-indigo-400 rounded-xl cursor-pointer hover:bg-slate-50/30 transition">
                    <div className="flex flex-col items-center justify-center gap-1.5 text-center p-4">
                      <Upload size={20} className="text-slate-400" />
                      <span className="text-xs font-semibold text-slate-600">Click to upload signature</span>
                      <span className="text-[10px] text-slate-400">PNG or JPG up to 500 KB</span>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  </label>
                )}
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl border border-slate-200 transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 py-2.5 bg-ctb-blue hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {isSubmitting && <Loader2 size={12} className="animate-spin" />}
                Counter-Sign NDA
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
